import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { isCreditsRequiredMessage } from '@/lib/creditsGuard';
import { trackCreditsRequiredUiAction } from '@/lib/creditsTracking';
import { CREDIT_PURCHASE_URL } from '@/lib/creditPurchase';

interface StudioBuildFailPanelProps {
  reason?: string;
  onRetry?: () => void;
}

export const StudioBuildFailPanel = ({ reason, onRetry }: StudioBuildFailPanelProps) => {
  const { t } = useTranslation('studio');
  const failureReason =
    reason ||
    t(
      'build.fail.default_reason',
      'The generator timed out while assembling the components for this page.',
    );
  const isCreditsFailure = isCreditsRequiredMessage(failureReason);
  const retryButtonClassName = isCreditsFailure
    ? 'inline-flex items-center gap-2 rounded-[11px] border border-[rgba(220,38,38,0.18)] bg-white p-[11px_16px] text-[13px] font-bold text-[#B91C1C] no-underline shadow-[0_10px_22px_rgba(220,38,38,0.08)] transition-all hover:-translate-y-[1px] hover:border-[rgba(220,38,38,0.26)] hover:bg-[rgba(254,242,242,0.92)] active:opacity-90'
    : 'inline-flex items-center gap-2 rounded-[11px] bg-gradient-to-br from-[#DC2626] to-[#F97316] p-[11px_16px] text-[13px] font-bold text-white no-underline shadow-[0_10px_22px_rgba(220,38,38,0.18)] transition-all hover:-translate-y-[1px] hover:shadow-[0_12px_24px_rgba(220,38,38,0.22)] active:opacity-90';

  return (
    <div className="mt-2 grid gap-[14px] rounded-[14px] border border-[rgba(220,38,38,0.15)] bg-gradient-to-b from-[rgba(255,255,255,0.98)] to-[rgba(254,242,242,0.95)] p-[18px] shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
      <div className="flex items-start gap-3">
        <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[rgba(220,38,38,0.08)] text-lg text-[#DC2626]">
          <Icon icon="fa6-solid:circle-exclamation" className="text-[18px]" />
        </div>
        <div>
          <div className="mb-1 text-base font-bold text-[#0D0D1A]">
            {t('build.fail.title', 'We could not finish this draft')}
          </div>
          <p className="m-0 text-[13px] leading-[1.65] text-[rgba(13,13,26,0.56)]">
            {t(
              'build.fail.description',
              'The build stopped before the first draft was ready. Review the failure reason below, then retry the page build when you are ready.',
            )}
          </p>
        </div>
      </div>
      <div className="rounded-xl border border-[rgba(220,38,38,0.12)] bg-[rgba(255,255,255,0.9)] p-[14px]">
        <div className="mb-[7px] text-[11px] font-bold tracking-[0.4px] text-[#B91C1C] uppercase">
          {t('build.fail.reason_label', 'Failure reason')}
        </div>
        <div className="text-[13px] leading-[1.7] text-[#0D0D1A]">{failureReason}</div>
      </div>
      <div className="flex flex-wrap items-center gap-[10px]">
        {isCreditsFailure ? (
          <a
            href={CREDIT_PURCHASE_URL}
            onClick={() => {
              trackCreditsRequiredUiAction({
                productArea: 'studio',
                surface: 'studio_build_failure',
                stage: 'page_build',
                uiType: 'inline',
                source: 'studio_build_fail_panel',
                operation: 'page_generation',
                trigger: 'build_failure',
                title: t('build.fail.credits_title', 'Credits required'),
                copy: failureReason,
                action: 'get_credits',
                targetRoute: CREDIT_PURCHASE_URL,
              });
            }}
            className="inline-flex items-center gap-2 rounded-[11px] bg-gradient-to-br from-[#6D28D9] to-[#0891B2] p-[11px_16px] text-[13px] font-bold text-white no-underline shadow-[0_10px_22px_rgba(109,40,217,0.18)] transition-all hover:-translate-y-[1px] hover:shadow-[0_12px_24px_rgba(8,145,178,0.2)] active:opacity-90"
          >
            <Icon icon="fa6-solid:coins" className="text-[14px]" />
            {t('build.fail.buy_credits_btn', 'Buy credits')}
          </a>
        ) : null}
        <button type="button" onClick={onRetry} className={retryButtonClassName}>
          <Icon icon="fa6-solid:rotate-right" className="text-[14px]" />
          {t('build.fail.retry_btn', 'Retry build')}
        </button>
        <div className="text-xs leading-[1.6] text-[rgba(13,13,26,0.38)]">
          {isCreditsFailure
            ? t(
                'build.fail.credits_help_text',
                'Top up credits first, then return to Studio and retry this build.',
              )
            : t(
                'build.fail.help_text',
                'Retry keeps your existing brief and starts a fresh build attempt.',
              )}
        </div>
      </div>
    </div>
  );
};
